import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon as MuiListItemIcon,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Refresh,
  Edit,
  Delete,
  MoreVert,
  Search,
  Map,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import DeleteConfirm from "../../modals/DeleteConfirm";
import {
  getAllServiceLocations,
  deleteServiceLocation,
} from "../../services/Api";

const GetAllServiceLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedLoc, setMenuSelectedLoc] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState(null);

  const navigate = useNavigate();

  const filteredLocations = locations.filter(
    (loc) =>
      (loc.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(loc.id || loc._id || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase()),
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 800);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllServiceLocations(
        page + 1,
        rowsPerPage,
        debouncedSearch
      );
      const src = response.data || response;
      const targetArray = Array.isArray(src) ? src : src.docs || src.serviceLocations || src.data || [];
      const total = response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || targetArray.length || 0;
      setLocations(targetArray);
      setTotalRows(total);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "An error occurred while fetching");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch]);

  const handleMenuOpen = (event, loc) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedLoc(loc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSelectedLoc(null);
  };

  const handleDeleteClick = () => {
    setSelectedLoc(menuSelectedLoc);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const response = await deleteServiceLocation(
        selectedLoc.id || selectedLoc._id,
      );
      if (response.success) {
        setDeleteDialogOpen(false);
        fetchLocations();
      } else {
        console.error("Failed to delete service location");
      }
    } catch (err) {
      console.error("Error deleting location:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 300 }}>
            Service Locations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Geofenced operational zones
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Search regions..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: 250,
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "rgba(0,0,0,0.02)",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={fetchLocations}
            disabled={loading}
            sx={{
              color: "primary.main",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Refresh />
            )}
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/service-location/create")}
            sx={{ px: 4, backgroundColor: "primary.main" }}
          >
            Create Zone
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "text.border",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
              <TableRow>
                {/* <TableCell sx={{ fontWeight: 700 }}>ID</TableCell> */}
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Polygon Points</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">
                      No locations found.{" "}
                      <Button
                        onClick={() => navigate("/service-location/create")}
                      >
                        Create one
                      </Button>
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLocations.map((loc) => (
                  <TableRow key={loc.id || loc._id} hover>
                    {/* <TableCell>{loc.id || loc._id}</TableCell> */}
                    <TableCell sx={{ fontWeight: 600 }}>{loc.name}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: loc.isActive ? "success.main" : "error.main",
                          backgroundColor: loc.isActive
                            ? "rgba(76, 175, 80, 0.1)"
                            : "rgba(244, 67, 54, 0.1)",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          display: "inline-block",
                          fontWeight: 600,
                        }}
                      >
                        {loc.isActive ? "Active" : "Inactive"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Map fontSize="small" color="action" />
                        <Typography variant="body2">
                          {loc.polygon?.length || 0} Anchors
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, loc)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={1}
        PaperProps={{
          sx: {
            minWidth: 150,
            border: "1px solid",
            borderColor: "text.border",
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            navigate(
              `/service-location/modify/${menuSelectedLoc?.id || menuSelectedLoc?._id}`,
            );
            handleMenuClose();
          }}
        >
          <MuiListItemIcon>
            <Edit fontSize="small" color="info" />
          </MuiListItemIcon>
          Edit Polygon
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <MuiListItemIcon>
            <Delete fontSize="small" color="error" />
          </MuiListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {deleteDialogOpen && (
        <DeleteConfirm
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Service Location"
          description={`Are you sure you want to delete ${selectedLoc?.name}?`}
          loading={loading}
        />
      )}
    </Box>
  );
};
export default GetAllServiceLocations;
